import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { formatRp, parseRupiah, formatRupiahInput, formatDateID } from "@/lib/constants";
import { Package, Plus, Trash, Receipt, MapPin } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import MidtransPayBlock from "@/components/MidtransPayBlock";
import RoomChatButton from "@/components/RoomChatButton";
import { NoAutoCancelToggle } from "@/components/NoAutoCancelToggle";
import { useHomecareRooms } from "@/hooks/useHomecareRooms";
import { useAuth } from "@/context/AuthContext";

const inp = "w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-[#7C4DE0]";
const addMin = (jam, min) => {
  if (!jam) return "";
  const [h, m] = jam.split(":").map(Number);
  const t = h * 60 + m + (Number(min) || 0);
  return `${String(Math.floor(t / 60) % 24).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [services, setServices] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [o, s, st] = await Promise.all([api.get("/orders"), api.get("/services"), api.get("/staff")]);
      setOrders(o.data); setServices(s.data); setStaff(st.data);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openDetail = async (id) => {
    try { const { data } = await api.get(`/orders/${id}`); setDetail(data); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="animate-fade-up space-y-5" data-testid="orders-page">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Package size={26} weight="duotone" className="text-[#7C4DE0]" />
          <h1 className="font-heading text-3xl font-extrabold text-slate-800">Pesanan Multi-Layanan</h1>
        </div>
        <button data-testid="create-order-btn" onClick={() => setCreateOpen(true)} className="inline-flex items-center gap-2 rounded-xl bg-[#7C4DE0] px-4 py-2.5 text-sm font-bold text-white hover:bg-[#6A38C7]"><Plus size={16} weight="bold" /> Buat Pesanan</button>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="h-8 w-8 animate-spin rounded-full border-4 border-[#E0D2FF] border-t-[#7C4DE0]" /></div>
      ) : orders.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#E0D2FF] bg-white/50 p-12 text-center text-sm text-slate-500">Belum ada pesanan multi-layanan.</div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {orders.map((o) => (
            <button key={o.id} data-testid={`order-card-${o.id}`} onClick={() => openDetail(o.id)} className="rounded-2xl border border-[#E0D2FF] bg-white p-4 text-left shadow-sm transition hover:border-[#7C4DE0] hover:shadow">
              <div className="flex items-center justify-between">
                <span className="font-heading font-extrabold text-[#6A38C7]">{o.order_no}</span>
                <PayBadge status={!o.is_estimate && Number(o.grand_total || 0) <= 0 ? "Tanpa Bayar" : o.payment_status} />
              </div>
              <p className="mt-1 font-bold text-slate-800">{o.nama_kontak}</p>
              <p className="text-xs text-slate-400">{formatDateID(o.tanggal_kunjungan)} · {o.lokasi} · {(o.item_ids || []).length} layanan</p>
              <p className="mt-2 text-sm font-extrabold text-slate-800">{formatRp(o.grand_total)}</p>
            </button>
          ))}
        </div>
      )}

      {createOpen && <CreateOrderDialog services={services} staff={staff} onClose={() => setCreateOpen(false)} onDone={(o) => { setCreateOpen(false); load(); openDetail(o.id); }} />}
      {detail && <OrderDetailDialog order={detail} onClose={() => setDetail(null)} onChange={(o) => { setDetail(o); load(); }} />}
    </div>
  );
}

function PayBadge({ status }) {
  const map = { "Lunas": "bg-emerald-100 text-emerald-700", "DP": "bg-amber-100 text-amber-700", "DP Terbayar": "bg-amber-100 text-amber-700", "Menunggu Pembayaran": "bg-sky-100 text-sky-700", "Menunggu Konfirmasi Admin": "bg-purple-100 text-purple-700", "Tanpa Bayar": "bg-slate-100 text-slate-600", "Belum Bayar": "bg-rose-100 text-rose-700" };
  return <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-bold ${map[status] || "bg-slate-100 text-slate-600"}`}>{status}</span>;
}

function ConfirmBillForm({ order, onChange, isRevision, onCancelEdit }) {
  const [vf, setVf] = useState(order.visit_fee ? formatRupiahInput(order.visit_fee) : "");
  const [dtype, setDtype] = useState(order.diskon_type && order.diskon_type !== "none" ? order.diskon_type : "none");
  const [dval, setDval] = useState(order.diskon_value ? (order.diskon_type === "nominal" ? formatRupiahInput(order.diskon_value) : String(order.diskon_value)) : "");
  const [term, setTerm] = useState(order.payment_term || "lunas");
  const [dp, setDp] = useState(order.dp_required ? formatRupiahInput(order.dp_required) : "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/confirm-bill`, {
        visit_fee: parseRupiah(vf), diskon_type: dtype,
        diskon_value: dtype === "nominal" ? parseRupiah(dval) : Number(dval || 0),
        payment_term: term, dp_required: parseRupiah(dp), reason,
      });
      toast.success(isRevision ? "Tagihan direvisi" : "Tagihan dikonfirmasi");
      onChange(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-purple-200 bg-purple-50/50 p-3" data-testid="confirm-bill-form">
      <p className="mb-2 text-xs font-bold uppercase tracking-wide text-purple-700">{isRevision ? "Revisi Tagihan" : "Konfirmasi Tagihan (dari estimasi klien)"}</p>
      <div className="grid grid-cols-2 gap-2">
        {order.lokasi === "Homecare" && <input data-testid="cb-visitfee" inputMode="numeric" value={vf} onChange={(e) => setVf(formatRupiahInput(e.target.value))} placeholder="Visit fee (1x)" className={inp} />}
        <select data-testid="cb-disc-type" value={dtype} onChange={(e) => setDtype(e.target.value)} className={inp}><option value="none">Tanpa Diskon</option><option value="nominal">Diskon Rp</option><option value="persen">Diskon %</option></select>
        {dtype !== "none" && <input data-testid="cb-disc-value" inputMode="numeric" value={dval} onChange={(e) => setDval(dtype === "nominal" ? formatRupiahInput(e.target.value) : e.target.value)} placeholder={dtype === "nominal" ? "Nominal" : "%"} className={inp} />}
        <select data-testid="cb-term" value={term} onChange={(e) => setTerm(e.target.value)} className={inp}><option value="lunas">Bayar Lunas</option><option value="dp">DP</option><option value="none">Tanpa Pembayaran</option></select>
        {term === "dp" && <input data-testid="cb-dp" inputMode="numeric" value={dp} onChange={(e) => setDp(formatRupiahInput(e.target.value))} placeholder="Nominal DP" className={inp} />}
      </div>
      {isRevision && <input data-testid="cb-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Alasan revisi (opsional)" className={`${inp} mt-2`} />}
      <div className="mt-2 flex gap-2">
        <button data-testid="cb-submit" onClick={submit} disabled={busy} className="flex-1 rounded-xl bg-[#7C4DE0] px-4 py-2 text-sm font-bold text-white disabled:opacity-50">{isRevision ? "Simpan Revisi" : "Konfirmasi Tagihan"}</button>
        {isRevision && <button data-testid="cb-cancel-edit" onClick={onCancelEdit} className="rounded-xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>}
      </div>
    </div>
  );
}

function CreateOrderDialog({ services, staff, onClose, onDone }) {
  const [f, setF] = useState({ nama_kontak: "", no_whatsapp: "", lokasi: "Gerai", alamat: "", tanggal: "", visit_fee: "", diskon_type: "none", diskon_value: "" });
  const [items, setItems] = useState([{ jenis_layanan: "", petugas_nama: "", jam_kunjungan: "", durasi: 60, harga: "", nama_pasien: "" }]);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const setItem = (i, k, v) => setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const onPickService = (i, nama) => {
    const svc = services.find((s) => s.nama === nama);
    setItems((arr) => arr.map((it, idx) => idx === i ? { ...it, jenis_layanan: nama, harga: svc?.harga_normal ? formatRupiahInput(svc.harga_normal) : it.harga, durasi: svc?.durasi_menit || svc?.durasi || it.durasi } : it));
  };
  const addItem = () => setItems((arr) => {
    const last = arr[arr.length - 1];
    const nextJam = last?.jam_kunjungan ? addMin(last.jam_kunjungan, last.durasi) : "";
    return [...arr, { jenis_layanan: "", petugas_nama: last?.petugas_nama || "", jam_kunjungan: nextJam, durasi: 60, harga: "", nama_pasien: "" }];
  });
  const removeItem = (i) => setItems((arr) => arr.length > 1 ? arr.filter((_, idx) => idx !== i) : arr);

  const subtotal = items.reduce((s, it) => s + parseRupiah(it.harga), 0);
  const visitFee = f.lokasi === "Homecare" ? parseRupiah(f.visit_fee) : 0;
  const dv = f.diskon_type === "nominal" ? parseRupiah(f.diskon_value) : Number(f.diskon_value || 0);
  const diskon = f.diskon_type === "nominal" ? Math.min(dv, subtotal) : f.diskon_type === "persen" ? Math.round(subtotal * Math.min(dv, 100) / 100) : 0;
  const grand = Math.max(0, subtotal + visitFee - diskon);

  const submit = async () => {
    if (!f.nama_kontak || !f.no_whatsapp || !f.tanggal) return toast.error("Lengkapi kontak & tanggal");
    if (items.some((it) => !it.jenis_layanan || !parseRupiah(it.harga))) return toast.error("Lengkapi layanan & harga tiap item");
    setBusy(true);
    try {
      const payload = {
        nama_kontak: f.nama_kontak, no_whatsapp: f.no_whatsapp, lokasi: f.lokasi, alamat: f.alamat, tanggal: f.tanggal,
        visit_fee: visitFee, diskon_type: f.diskon_type, diskon_value: f.diskon_type === "nominal" ? parseRupiah(f.diskon_value) : Number(f.diskon_value || 0),
        items: items.map((it) => ({ ...it, harga: parseRupiah(it.harga), durasi: Number(it.durasi) || 60 })),
      };
      const { data } = await api.post("/orders", payload);
      toast.success(`Pesanan ${data.order_no} dibuat`);
      onDone(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-2xl" data-testid="create-order-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading">Buat Pesanan Multi-Layanan</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2">
            <input data-testid="order-kontak" value={f.nama_kontak} onChange={(e) => set("nama_kontak", e.target.value)} placeholder="Nama kontak utama" className={inp} />
            <input data-testid="order-wa" value={f.no_whatsapp} onChange={(e) => set("no_whatsapp", e.target.value)} placeholder="No. WhatsApp" className={inp} />
            <select data-testid="order-lokasi" value={f.lokasi} onChange={(e) => set("lokasi", e.target.value)} className={inp}>
              <option value="Gerai">Gerai / Klinik</option>
              <option value="Homecare">Homecare</option>
            </select>
            <input data-testid="order-tanggal" type="date" value={f.tanggal} onChange={(e) => set("tanggal", e.target.value)} className={inp} />
          </div>
          {f.lokasi === "Homecare" && (
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="order-alamat" value={f.alamat} onChange={(e) => set("alamat", e.target.value)} placeholder="Alamat lengkap" className={inp} />
              <input data-testid="order-visitfee" inputMode="numeric" value={f.visit_fee} onChange={(e) => set("visit_fee", formatRupiahInput(e.target.value))} placeholder="Visit fee (1x)" className={inp} />
            </div>
          )}

          <div className="space-y-2">
            <p className="text-xs font-bold uppercase tracking-wide text-slate-400">Layanan / Pasien</p>
            {items.map((it, i) => (
              <div key={i} data-testid={`order-item-${i}`} className="rounded-xl border border-slate-200 p-2">
                <div className="grid grid-cols-2 gap-2">
                  <select data-testid={`item-service-${i}`} value={it.jenis_layanan} onChange={(e) => onPickService(i, e.target.value)} className={inp}>
                    <option value="">Pilih Layanan</option>
                    {services.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}
                  </select>
                  <input data-testid={`item-pasien-${i}`} value={it.nama_pasien} onChange={(e) => setItem(i, "nama_pasien", e.target.value)} placeholder="Nama pasien" className={inp} />
                  <select data-testid={`item-staff-${i}`} value={it.petugas_nama} onChange={(e) => setItem(i, "petugas_nama", e.target.value)} className={inp}>
                    <option value="">Petugas (opsional)</option>
                    {staff.map((s) => <option key={s.id} value={s.nama}>{s.nama}</option>)}
                  </select>
                  <div className="grid grid-cols-3 gap-1">
                    <input data-testid={`item-jam-${i}`} type="time" value={it.jam_kunjungan} onChange={(e) => setItem(i, "jam_kunjungan", e.target.value)} className={inp} title="Jam" />
                    <input data-testid={`item-durasi-${i}`} type="number" value={it.durasi} onChange={(e) => setItem(i, "durasi", e.target.value)} className={inp} title="Durasi (menit)" />
                    <input data-testid={`item-harga-${i}`} inputMode="numeric" value={it.harga} onChange={(e) => setItem(i, "harga", formatRupiahInput(e.target.value))} placeholder="Harga" className={inp} />
                  </div>
                </div>
                {items.length > 1 && <button data-testid={`item-remove-${i}`} onClick={() => removeItem(i)} className="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-rose-500"><Trash size={12} /> Hapus item</button>}
              </div>
            ))}
            <button data-testid="add-item-btn" onClick={addItem} className="inline-flex items-center gap-1.5 rounded-xl bg-[#EEE6FF] px-3 py-2 text-xs font-bold text-[#6A38C7] hover:bg-[#e0d2ff]"><Plus size={14} weight="bold" /> Tambah Layanan</button>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <select data-testid="order-disc-type" value={f.diskon_type} onChange={(e) => set("diskon_type", e.target.value)} className={inp}>
              <option value="none">Tanpa Diskon</option>
              <option value="nominal">Diskon Nominal (Rp)</option>
              <option value="persen">Diskon Persen (%)</option>
            </select>
            {f.diskon_type !== "none" && (
              f.diskon_type === "nominal"
                ? <input data-testid="order-disc-value" inputMode="numeric" value={f.diskon_value} onChange={(e) => set("diskon_value", formatRupiahInput(e.target.value))} placeholder="Nominal diskon" className={inp} />
                : <input data-testid="order-disc-value" type="number" value={f.diskon_value} onChange={(e) => set("diskon_value", e.target.value)} placeholder="% diskon" className={inp} />
            )}
          </div>

          <div className="rounded-xl border border-[#E0D2FF] bg-[#FAF7FF] p-3 text-sm">
            <Row l="Subtotal layanan" v={formatRp(subtotal)} />
            {f.lokasi === "Homecare" && <Row l="Visit fee (1x)" v={formatRp(visitFee)} />}
            <Row l="Diskon" v={`− ${formatRp(diskon)}`} />
            <div className="mt-1 flex justify-between border-t border-[#E0D2FF] pt-1"><span className="font-bold text-slate-600">Grand Total</span><span data-testid="order-grand-total" className="font-extrabold text-[#7C4DE0]">{formatRp(grand)}</span></div>
          </div>
        </div>
        <DialogFooter>
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Batal</button>
          <button data-testid="submit-order" onClick={submit} disabled={busy} className="rounded-xl bg-[#7C4DE0] px-5 py-2 text-sm font-bold text-white disabled:opacity-50">Buat Pesanan</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function OrderDetailDialog({ order, onClose, onChange }) {
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("Tunai");
  const [kind, setKind] = useState("dp");
  const [busy, setBusy] = useState(false);
  const [revise, setRevise] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [editC, setEditC] = useState(null);
  const [resendAsk, setResendAsk] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const { user } = useAuth();
  const { byReservation } = useHomecareRooms();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const o = order;

  const reloadOrder = async () => {
    try { const { data } = await api.get(`/orders/${o.id}`); onChange(data); }
    catch (e) { /* ignore */ }
  };

  const pay = async () => {
    const amt = parseRupiah(amount);
    if (!amt) return toast.error("Nominal tidak valid");
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.id}/manual-payment`, { amount: amt, method, kind });
      toast.success("Pembayaran tercatat");
      setAmount("");
      onChange(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resendLink = async () => {
    setResending(true);
    try {
      const { data } = await api.post(`/orders/${o.id}/resend-payment-link`);
      const st = data.wa_status;
      if (st === "sent") toast.success("Link pembayaran terkirim via WhatsApp");
      else if (st === "stubbed") toast.success("Link tercatat (mode uji — WA belum aktif)");
      else if (st === "invalid_phone") toast.error("Nomor WhatsApp tidak valid");
      else toast.error("Gagal mengirim WhatsApp (WACloud). Coba lagi.");
    } catch (e) { toast.error(apiError(e)); } finally { setResending(false); }
  };

  const cancelOrder = async () => {    if (!window.confirm("Batalkan pesanan ini? Seluruh slot/jadwal terkait akan dilepas.")) return;
    setCancelBusy(true);
    try {
      await api.post(`/orders/${o.id}/cancel`, { reason: "Dibatalkan admin" });
      toast.success("Pesanan dibatalkan, slot dilepas");
      onChange(null); onClose();
    } catch (e) { toast.error(apiError(e)); } finally { setCancelBusy(false); }
  };

  const saveContact = async () => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${o.id}/contact`, editC);
      toast.success("Kontak pesanan diperbarui"); setEditC(null); setJustSaved(true); onChange(data);
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const resendConfirm = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/orders/${o.id}/resend-confirmation`, { confirm: true });
      if (data.wa_status === "sent" || data.wa_status === "accepted_by_gateway") toast.success("Konfirmasi terkirim ke nomor baru");
      else if (data.wa_status === "stubbed") toast.success("Konfirmasi tercatat (mode uji — WA belum aktif)");
      else toast.error("Gagal mengirim WhatsApp. Coba lagi.");
      setResendAsk(false); setJustSaved(false); reloadOrder();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };

  const isFree = !o.is_estimate && Number(o.grand_total || 0) <= 0;
  const termLabel = { lunas: "Bayar Lunas", dp: "DP", none: "Tanpa Pembayaran" }[o.payment_term] || "-";
  const waMsg = () => {
    const lines = [`Halo ${o.nama_kontak}, berikut rincian tagihan pesanan *${o.order_no}* — GBS Clinic:`, ""];
    (o.items || []).forEach((it) => lines.push(`• ${it.jenis_layanan}${it.nama_lengkap ? ` (${it.nama_lengkap})` : ""}${it.jam_kunjungan ? ` — ${it.jam_kunjungan}` : ""}: ${formatRp(it.total_tagihan)}`));
    lines.push("", `Subtotal: ${formatRp(o.subtotal)}`);
    if (o.visit_fee > 0) lines.push(`Biaya kunjungan (1x): ${formatRp(o.visit_fee)}`);
    if (o.diskon_nominal > 0) lines.push(`Diskon: − ${formatRp(o.diskon_nominal)}`);
    lines.push(`*Total: ${formatRp(o.grand_total)}*`);
    if (o.payment_term === "dp" && o.dp_required > 0) lines.push(`DP: ${formatRp(o.dp_required)}`, `Sisa: ${formatRp(o.sisa_tagihan)}`);
    lines.push(`Metode pembayaran: ${termLabel}`, "", "Terima kasih 🙏 — GBS Clinic");
    return lines.join("\n");
  };
  const waHref = `https://api.whatsapp.com/send?phone=${String(o.no_whatsapp || "").replace(/[^0-9]/g, "").replace(/^0/, "62")}&text=${encodeURIComponent(waMsg())}`;

  return (
    <Dialog open onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-h-[92vh] overflow-y-auto rounded-2xl sm:max-w-lg" data-testid="order-detail-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Receipt size={18} className="text-[#7C4DE0]" /> {o.order_no}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <div>
            {editC ? (
              <div data-testid="order-contact-edit" className="space-y-2 rounded-xl border border-[#E0D2FF] bg-[#FAF7FF] p-3">
                <input data-testid="order-contact-name" value={editC.nama_kontak} onChange={(e) => setEditC({ ...editC, nama_kontak: e.target.value })} placeholder="Nama kontak" className={inp} />
                <input data-testid="order-contact-wa" value={editC.no_whatsapp} onChange={(e) => setEditC({ ...editC, no_whatsapp: e.target.value })} placeholder="08xxxxxxxxxx" inputMode="tel" className={inp} />
                <p className="text-[11px] text-slate-500">Nomor WA semua reservasi di pesanan ini ikut diperbarui; nama pasien ikut diganti bila sama dengan nama kontak lama.</p>
                <div className="flex gap-2">
                  <button data-testid="order-contact-save" disabled={busy} onClick={saveContact} className="rounded-lg bg-[#7C4DE0] px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">Simpan</button>
                  <button data-testid="order-contact-cancel" onClick={() => setEditC(null)} className="rounded-lg bg-white px-3 py-1.5 text-xs font-semibold text-slate-600 ring-1 ring-slate-200">Batal</button>
                </div>
              </div>
            ) : (
              <p className="flex flex-wrap items-center gap-2 font-bold text-slate-800">{o.nama_kontak} · {o.no_whatsapp}
                {isAdmin && <button data-testid="order-contact-edit-btn" onClick={() => setEditC({ nama_kontak: o.nama_kontak || "", no_whatsapp: o.no_whatsapp || "" })} className="rounded-lg bg-[#F3ECFF] px-2 py-0.5 text-[11px] font-bold text-[#6A38C7]">Edit kontak</button>}
              </p>
            )}
            {isAdmin && !editC && (justSaved || resendAsk) && (
              <div data-testid="order-resend-box" className="mt-1 rounded-xl border border-emerald-200 bg-emerald-50 p-2.5 text-xs text-emerald-800">
                {!resendAsk ? (
                  <button data-testid="order-resend-confirm-btn" onClick={() => setResendAsk(true)} className="rounded-lg bg-emerald-600 px-3 py-1.5 font-bold text-white">Kirim ulang konfirmasi ke nomor baru</button>
                ) : (
                  <span className="flex flex-wrap items-center gap-2">Kirim konfirmasi + link status pesanan{" "}(+ link room chat Homecare) ke {o.no_whatsapp}?
                    <button data-testid="order-resend-yes" disabled={busy} onClick={resendConfirm} className="rounded bg-emerald-600 px-2 py-1 font-bold text-white disabled:opacity-60">Ya, kirim</button>
                    <button data-testid="order-resend-no" onClick={() => setResendAsk(false)} className="rounded bg-white px-2 py-1 font-semibold text-slate-600">Batal</button>
                  </span>
                )}
              </div>
            )}
            {(o.contact_history || []).length > 0 && <p data-testid="order-contact-history" className="mt-1 text-xs font-medium text-slate-600">Riwayat kontak: {o.contact_history.slice(-3).map((h) => `${h.from?.nama_kontak || "-"} / ${h.from?.no_whatsapp || "-"} → ${h.to?.nama_kontak} / ${h.to?.no_whatsapp} (${h.by || "-"})`).join("; ")}</p>}
            <p className="text-xs text-slate-400">{formatDateID(o.tanggal_kunjungan)} · {o.lokasi}</p>
            {o.lokasi === "Homecare" && o.alamat && <p className="mt-1 flex items-start gap-1 text-xs text-slate-500"><MapPin size={13} className="mt-0.5" /> {o.alamat}</p>}
          </div>
          <div className="space-y-1.5">
            {!o.auto_cancelled && <NoAutoCancelToggle kind="order" id={o.id} initial={o.no_auto_cancel} />}
            {o.items?.map((it) => (
              <div key={it.id} data-testid={`detail-item-${it.id}`} className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                <div className="min-w-0"><p className="font-semibold text-slate-800">{it.jenis_layanan}</p><p className="text-xs text-slate-400">{it.nama_lengkap}{it.petugas_nama ? ` · ${it.petugas_nama}` : ""}{it.jam_kunjungan ? ` · ${it.jam_kunjungan}` : ""}</p></div>
                <div className="flex shrink-0 items-center gap-2">
                  <RoomChatButton reservation={it} roomInfo={byReservation[String(it.id)]} />
                  <span className="font-bold text-slate-700">{formatRp(it.total_tagihan)}</span>
                </div>
              </div>
            ))}
          </div>
          <div className="rounded-xl border border-[#E0D2FF] bg-[#FAF7FF] p-3">
            <Row l="Subtotal" v={formatRp(o.subtotal)} />
            {o.visit_fee > 0 && <Row l="Visit fee" v={formatRp(o.visit_fee)} />}
            {o.diskon_nominal > 0 && <Row l="Diskon" v={`− ${formatRp(o.diskon_nominal)}`} />}
            <Row l={o.is_estimate ? "Estimasi Biaya" : "Grand Total"} v={formatRp(o.grand_total)} bold />
            {!o.is_estimate && o.payment_term === "dp" && o.dp_required > 0 && <Row l="DP Diminta" v={formatRp(o.dp_required)} />}
            {!o.is_estimate && <Row l="Sudah Dibayar" v={formatRp(o.total_dibayar)} />}
            {!o.is_estimate && <Row l="Sisa Tagihan" v={formatRp(o.sisa_tagihan)} />}
            <div className="mt-1 flex items-center justify-between"><span data-testid="order-pay-status"><PayBadge status={isFree ? "Tanpa Bayar" : o.payment_status} /></span><span className="text-xs text-slate-400">{o.is_estimate ? "" : `Termin: ${termLabel}`}</span></div>
          </div>

          {!o.is_estimate && !isFree && o.public_token && (
            <div className="rounded-xl border border-indigo-200 bg-indigo-50 p-3" data-testid="order-payment-link">
              <p className="mb-1 text-xs font-bold uppercase tracking-wide text-indigo-700">Link Pembayaran Pasien</p>
              <p className="break-all text-[11px] text-slate-600" data-testid="order-payment-link-url">{`${window.location.origin}/order/${o.public_token}`}</p>
              <div className="mt-2 flex flex-wrap gap-2">
                <button data-testid="order-copy-link" onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/order/${o.public_token}`); toast.success("Link pembayaran disalin"); }} className="rounded-xl bg-indigo-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-indigo-700">Salin Link</button>
                <a data-testid="order-send-wa-link" href={`https://api.whatsapp.com/send?phone=${String(o.no_whatsapp || "").replace(/\D/g, "").replace(/^0/, "62")}&text=${encodeURIComponent(`Pesanan GBS Anda (${o.order_no}) telah dikonfirmasi. Total tagihan ${formatRp(o.grand_total)}${o.payment_term === "dp" && o.dp_required > 0 ? ` / DP ${formatRp(o.dp_required)}` : ""}. Silakan lihat rincian & bayar di: ${window.location.origin}/order/${o.public_token}`)}`} target="_blank" rel="noreferrer" className="rounded-xl bg-emerald-500 px-3 py-1.5 text-xs font-bold text-white hover:bg-emerald-600">Kirim via WhatsApp</a>
                <button data-testid="order-resend-link" onClick={resendLink} disabled={resending} className="rounded-xl border border-emerald-500 px-3 py-1.5 text-xs font-bold text-emerald-700 hover:bg-emerald-50 disabled:opacity-50">{resending ? "Mengirim…" : "Kirim Ulang Link Pembayaran"}</button>
              </div>
            </div>
          )}

          {o.is_estimate ? (
            <ConfirmBillForm order={o} onChange={onChange} />
          ) : revise ? (
            <ConfirmBillForm order={o} onChange={(d) => { setRevise(false); onChange(d); }} isRevision onCancelEdit={() => setRevise(false)} />
          ) : (
            <div className="space-y-2">
              {!isFree && <a data-testid="order-wa-btn" href={waHref} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-bold text-white hover:bg-emerald-600">Kirim Tagihan via WhatsApp</a>}
              <button data-testid="order-revise-btn" onClick={() => setRevise(true)} className="w-full rounded-xl border border-purple-300 px-4 py-2 text-sm font-bold text-purple-700 hover:bg-purple-50">Revisi Tagihan</button>
            </div>
          )}

          {!o.is_estimate && !isFree && o.payment_term !== "none" && o.payment_status !== "Lunas" && !revise && (
            <div className="rounded-xl border border-slate-200 p-3">
              <p className="mb-2 text-xs font-bold uppercase tracking-wide text-slate-400">Catat Pembayaran (1 checkout)</p>
              <div className="grid grid-cols-3 gap-2">
                <input data-testid="order-pay-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(formatRupiahInput(e.target.value))} placeholder="Nominal" className={inp} />
                <select data-testid="order-pay-kind" value={kind} onChange={(e) => setKind(e.target.value)} className={inp}><option value="dp">DP</option><option value="pelunasan">Pelunasan</option></select>
                <input data-testid="order-pay-method" value={method} onChange={(e) => setMethod(e.target.value)} placeholder="Metode" className={inp} />
              </div>
              <button data-testid="order-pay-submit" onClick={pay} disabled={busy} className="mt-2 w-full rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-700 disabled:opacity-50">Catat Pembayaran</button>
              <MidtransPayBlock kind="order" id={o.id} sisa={o.sisa_tagihan} isAdmin={isAdmin}
                defaultAmount={o.payment_term === "dp"
                  ? (Math.max(0, Math.round((o.dp_required || 0) - (o.total_dibayar || 0))) || Math.round(o.sisa_tagihan || 0))
                  : Math.round(o.sisa_tagihan || 0)}
                onPaid={reloadOrder} />
            </div>
          )}

          {o.payments?.length > 0 && (
            <div className="space-y-1">
              <p className="text-xs font-bold text-slate-400">Riwayat Pembayaran</p>
              {o.payments.map((p) => <div key={p.id} className="flex justify-between text-xs text-slate-500"><span>{p.tanggal} · {p.method} · {p.kind}</span><span className="font-semibold text-emerald-600">{formatRp(p.amount)}</span></div>)}
            </div>
          )}

          {o.bill_history?.length > 0 && (
            <div className="space-y-1" data-testid="bill-history">
              <p className="text-xs font-bold text-slate-400">Riwayat Tagihan</p>
              {o.bill_history.map((h, i) => (
                <div key={i} className="rounded-lg bg-slate-50 px-2.5 py-1.5 text-[11px] text-slate-500">
                  <span className="font-semibold text-slate-600">{h.kind === "revision" ? "Revisi" : "Konfirmasi"}</span> · {String(h.at).slice(0, 16).replace("T", " ")} · {h.by_name || h.by}
                  <span className="block">{formatRp(h.before?.grand_total)} → <span className="font-semibold text-[#7C4DE0]">{formatRp(h.after?.grand_total)}</span>{h.reason ? ` · ${h.reason}` : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <DialogFooter className="flex-col-reverse gap-2 sm:flex-row sm:justify-between">
          {o.payment_status !== "Dibatalkan" && (o.is_estimate || o.total_dibayar === 0) && (
            <button data-testid="order-cancel-btn" onClick={cancelOrder} disabled={cancelBusy} className="rounded-xl px-4 py-2 text-sm font-semibold text-rose-500 hover:bg-rose-50 disabled:opacity-50">Batalkan Pesanan</button>
          )}
          <button onClick={onClose} className="rounded-xl px-4 py-2 text-sm font-semibold text-slate-500">Tutup</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Row({ l, v, bold }) {
  return <div className="flex justify-between"><span className={bold ? "font-bold text-slate-600" : "text-slate-500"}>{l}</span><span className={bold ? "font-extrabold text-[#7C4DE0]" : "font-semibold text-slate-800"}>{v}</span></div>;
}
